import type {
  PanelPagination,
  PulseCheckEntry,
} from '@/components/InsightsV2/MemberInsightsPage/types';
import type {
  PulseReportApiAnswer,
  PulseReportApiPagination,
  PulseReportApiReport,
} from '@/store/api/pulseApi';
import type {
  PulseReportAnswer,
  PulseReportPayload,
  PulseReportSection,
  PulseSectionKey,
} from '@/types';

const DEFAULT_RATING_MAX = 5;
const ONBOARDING_REVIEW_TITLE = 'Coach Matching & Baseline Survey';

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export const formatMonthYear = (input?: string | Date): string => {
  if (!input) {
    return 'Unknown date';
  }
  const parsed = new Date(input);
  if (Number.isNaN(parsed.getTime())) {
    return 'Unknown date';
  }
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });
};

export const toRangeBucket = (value: number): 'low' | 'mid' | 'high' => {
  if (value >= 8) {
    return 'high';
  }
  if (value >= 5) {
    return 'mid';
  }
  return 'low';
};

export const roundToOneDecimal = (value: number) => Math.round(value * 10) / 10;

const SECTION_DISPLAY_CONFIG: Record<
  PulseSectionKey,
  {
    order: number;
    title: string;
  }
> = {
  coach_assessment: {
    order: 4,
    title: 'Coach Efficacy',
  },
  leadership_skills: {
    order: 2,
    title: 'Leadership Skills',
  },
  manager_assessment: {
    order: 3,
    title: 'Manager Effectiveness',
  },
  performance_goals: {
    order: 1,
    title: 'Individual Performance',
  },
};

const getSectionDisplayConfig = (sectionKey: string) => {
  switch (sectionKey) {
    case 'performance_goals':
    case 'leadership_skills':
    case 'manager_assessment':
    case 'coach_assessment':
      return SECTION_DISPLAY_CONFIG[sectionKey];
    default:
      return undefined;
  }
};

const normalizeNumericAnswer = (
  answer: PulseReportApiAnswer,
): number | null => {
  if (answer.kind === 'range10') {
    return clamp(answer.value, 0, 10);
  }
  if (answer.kind === 'rating') {
    const max = answer.max > 0 ? answer.max : DEFAULT_RATING_MAX;
    return clamp((answer.value / max) * 10, 0, 10);
  }
  return null;
};

const calculateAverageScore = (
  answers: PulseReportApiAnswer[],
): number | null => {
  const normalizedAnswers = answers
    .map(normalizeNumericAnswer)
    .filter((value): value is number => value !== null);

  if (!normalizedAnswers.length) {
    return null;
  }

  return roundToOneDecimal(
    normalizedAnswers.reduce((total, value) => total + value, 0) /
      normalizedAnswers.length,
  );
};

const mapAnswerToPayload = (
  report: PulseReportApiReport,
  sectionKey: string,
  answer: PulseReportApiAnswer,
  answerIndex: number,
): PulseReportAnswer | null => {
  const base = {
    id: `${report.reportId}:${sectionKey}:${answer.questionId}:${answerIndex}`,
    order: answerIndex + 1,
    prompt: answer.label,
    questionKey: answer.questionId,
  };

  if (answer.kind === 'range10') {
    const normalizedValue = clamp(answer.value, 0, 10);
    return {
      ...base,
      bucket: toRangeBucket(normalizedValue),
      kind: 'range10',
      max: 10,
      min: 0,
      value: normalizedValue,
    };
  }

  if (answer.kind === 'freeform') {
    const text = answer.text.trim();
    if (!text) {
      return null;
    }
    return {
      ...base,
      author: {
        avatarUrl: report.member.avatarUrl ?? null,
        name: report.member.fullName,
      },
      kind: 'freeform',
      text,
    };
  }

  if (answer.kind === 'multi_select') {
    const selections = answer.options
      .map((option) => option.trim())
      .filter(Boolean);
    return {
      ...base,
      kind: 'multi_select',
      selections,
    };
  }

  const max = answer.max > 0 ? answer.max : DEFAULT_RATING_MAX;
  const normalizedValue = clamp(answer.value, 0, max);
  const scaledValue = Math.round((normalizedValue / max) * DEFAULT_RATING_MAX);
  return {
    ...base,
    kind: 'rating5',
    max: 5,
    value: scaledValue,
  };
};

const mapSectionsToPayload = (
  report: PulseReportApiReport,
): PulseReportSection[] =>
  report.sections.map((section, sectionIndex) => {
    const answers = section.answers
      .map((answer, answerIndex) =>
        mapAnswerToPayload(report, section.key, answer, answerIndex),
      )
      .filter((answer): answer is PulseReportAnswer => answer !== null);
    const displayConfig = getSectionDisplayConfig(section.key);
    const averageScore = calculateAverageScore(section.answers);

    return {
      answers,
      averageScore: averageScore === null ? undefined : averageScore,
      coachNameAtSubmission: section.coachNameAtSubmission,
      key: section.key as PulseReportSection['key'],
      order: displayConfig?.order ?? sectionIndex + 1,
      title: displayConfig?.title ?? section.title,
    };
  });

const flattenAnswers = (report: PulseReportApiReport): PulseReportApiAnswer[] =>
  report.sections.flatMap((section) => section.answers);

export const mapPulseReportToPayload = (
  report: PulseReportApiReport,
): PulseReportPayload => ({
  debrief: report.debrief ?? null,
  member: {
    avatarUrl: report.member.avatarUrl ?? null,
    fullName: report.member.fullName,
    isManager: false,
  },
  memberId: report.memberId,
  reportId: report.reportId,
  sections: mapSectionsToPayload(report),
  source: {
    formId: report.sourceVersion,
    responseId: report.reportId,
    schemaVersion: 'v1',
    vendor: 'typeform',
  },
  submittedAt: report.submittedAt,
});

interface MapPulseReportToListEntryOptions {
  includeOnboardingReviewPresentation?: boolean;
  onboardingReviewDetailPath?: (report: PulseReportApiReport) => string;
}

export const isOnboardingBaselineReport = (report: PulseReportApiReport) => {
  if (report.isOnboardingBaseline !== undefined) {
    return report.isOnboardingBaseline;
  }

  return (
    report.reportKind === 'onboarding_baseline' ||
    report.source?.formType === 'member_onboarding'
  );
};

export const mapPulseReportToListEntry = (
  report: PulseReportApiReport,
  options: MapPulseReportToListEntryOptions = {},
): PulseCheckEntry => {
  const answers = flattenAnswers(report);
  const answeredCount = answers.length;
  const numericAnswers = answers
    .map(normalizeNumericAnswer)
    .filter((value): value is number => value !== null);

  const averageScore = numericAnswers.length
    ? roundToOneDecimal(
        numericAnswers.reduce((total, value) => total + value, 0) /
          numericAnswers.length,
      )
    : 0;

  const isOnboardingBaseline = isOnboardingBaselineReport(report);
  const showOnboardingReviewPresentation =
    options.includeOnboardingReviewPresentation === true &&
    isOnboardingBaseline;

  return {
    answeredCount,
    averageScore,
    bannerWindowExpiresAt: report.debrief?.bannerWindowExpiresAt ?? null,
    canMarkDebriefed: report.debrief?.canMarkDebriefed,
    dateLabel: formatMonthYear(report.submittedAt),
    debriefStatus: report.debrief?.status,
    debriefedAt: report.debrief?.debriefedAt ?? null,
    detailPath: showOnboardingReviewPresentation
      ? (options.onboardingReviewDetailPath?.(report) ??
        `/m/onboarding-review/${report.reportId}`)
      : undefined,
    displayLabel: showOnboardingReviewPresentation
      ? ONBOARDING_REVIEW_TITLE
      : undefined,
    id: report.reportId,
    isOnboardingBaseline,
    relationshipStage: report.debrief?.relationshipStage,
    totalQuestions: answeredCount,
  };
};

export const mapPulsePagination = (
  pagination: PulseReportApiPagination | undefined,
): PanelPagination | undefined => {
  if (!pagination) {
    return undefined;
  }
  return {
    currentPage: pagination.currentPage,
    totalPages: pagination.totalPages,
  };
};
